import formatProductsData from './format-products-data-nosto';
import fieldsRequest from './fields-request';

/* 
This class is used to handle Nosto collections
Important: also update the fallback class in fallback.js if editing this one
*/
export default class NostoCollections {
  collection_id;
  fields;
  // products_per_page;

  constructor(collection_id) {
    this.collection_id = collection_id;
    // this.products_per_page = Number(window.theme.collections.products_per_page) || 50;  // Needs to be divisible by 2
    this.fields = fieldsRequest;
    // window.theme.collections.page_split = false;  // Load the 1st page in half the size to improve the loading time when on a collection page with no filters and sorting by manual
    window.theme.collections.filtering = [];
    window.theme.collections.sorting = 'manual';
  }

  /*
  // https://github.com/Nosto/nosto-js/blob/5e5dbdcd7b34e2dfb60a503c88017f00a1d0d1a8/src/client/nosto.d.ts#L2400
  Convert:
  [
    [
        "skus.customFields.size",
        "XS"
    ],
    [
        "skus.customFields.astr-color_family",
        "blue"
    ],
    [
        "skus.customFields.astr-color_family",
        "green"
    ],
    [
        "price",
        "50-75"
    ],
    [
        "price",
        "75-100"
    ],
    ['vt::neckline', 'Collared'],
    ['vt::neckline', 'Cowl'],
    ['vt::occasion', 'Brunch']
  ]

  To:
  [
    {
      'all': [
        { 'field': "skus.customFields.size", 'value': ["XS"] },
        { 'field': "skus.customFields.astr-color_family", 'value': ["blue", "green"] },
        {
          'field': "price",
          'range': [
            { 'gte': "50", 'lt': "75" },
            { 'gte': "75", 'lt': "100" },
          ],
        },
        {
          'field': "tags1",
          'value': ["vt::neckline::Collared", "vt::neckline::Cowl"],
        },
        { 'field': "tags1", 'value': ["vt::occasion::Brunch"] },
      ],
    },
  ]
  Similar to:
  [
    {
      'all': [
        {'any': [{'field': 'skus.customFields.size', 'value': ['XS']}]},
        {'any': [{'field': 'skus.customFields.astr-color_family', 'value': ['blue']}, {'field': 'skus.customFields.astr-color_family', 'value': ['green']}]},
        {'any': [{'field': 'price', 'range': [{'gte': '50', 'lt': '75'}, {'gte': '75', 'lt': '100'}]}]},
        {'any': [{'field': 'tags1', 'value': ['vt::neckline::Collared', 'vt::neckline::Cowl']}]},
        {'any': [{'field': 'tags1', 'value': ['vt::occasion::Brunch']}]},
      ]
    }
  ]
  */
  transformFilters = (filters) => {
    // Group by field
    const grouped = filters.reduce((acc, [field, value]) => {
      if (!acc[field]) {
        acc[field] = [];
      }
      acc[field].push(value);
      return acc;
    }, {});

    // Transform to final format
    const transformed = Object.entries(grouped).map(([field, values]) => {
      if (field === 'price') {
        return {
          field,
          range: values.map(value => {
            const [gte, lt] = value.split('-');
            return { gte, lt };
          })
        };
      } else if (field.startsWith('vt::')) {
        return {
          field: 'tags1',
          value: values.map(value => `${field}::${value}`)
        };
      } else {
        return {
          field,
          value: values
        };
      }
    });
  
    return (transformed.length > 1) ? [{ all: transformed }] : transformed;
  };

  /**
   * Retrieve the sort configuration based on the current sorting option. For collection page and search overlay.
   * 
   * @returns {Array} Sort configuration array
   */
  getSortConfig = () => {
    const from_search = document.body.classList.contains('has-overlay-search');

    const sortMapping = {
      'price-ascending': [{ field: 'price', order: 'asc' }],
      'price-descending': [{ field: 'price', order: 'desc' }],
      // 'title-ascending': [{ field: 'name', order: 'asc' }],
      // 'title-descending': [{ field: 'name', order: 'desc' }],
      'created-ascending': [{ field: 'datePublished', order: 'asc' }],
      'created-descending': [{ field: 'datePublished', order: 'desc' }],
      // 'popularity': [{ field: 'stats.views', order: 'desc' }],
      'manual': [],// [{ field: 'custom_ranking', order: 'desc' }]
      // 'reviews-rating_count': [{ field: 'reviews-rating_count', order: 'desc' }],
      // 'reviews-rating': [{ field: 'reviews-rating', order: 'desc' }],
    };
  
    return sortMapping[(from_search) ? window.theme.search.sorting : window.theme.collections.sorting] || [];
  };

  /**
   * Make Nosto API request
   * Doesn't apply to search overlay
   * 
   * @returns {Promise} Formatted products data
   */
  nostoRequest = () => {
    return new Promise((resolve) => {
      try {
        let filters = [];
        if (typeof window.theme.collections?.filtering !== 'undefined' && window.theme.collections?.filtering.length) {
          filters = window.theme.collections.filtering;
        }

        // Enforce plus size-only products on plus size collections
        if (document.querySelector('[data-collection-grid]')?.dataset?.plus === 'true') {
          filters.push(['skus.customFields.astr-plus_size', 'true']);
        }

        console.log('[ASTR] filters 1', filters);
        filters = (filters.length > 0) ? this.transformFilters(filters) : [];
        
        // Hide ambassaor products if not on the ambassador collection
        // Hide ambassaor products on the ambassador collection but the user is not an ambassador (in case someone hacks the redirect from the collection page)
        if ((window.theme.collections.current === 'ambassador' && !window.theme.customer.ambassador) || (window.theme.collections.current !== 'ambassador' && window.theme.collections.current !== 'early-access')) {
          filters.push({
            'not': [
              { 'field': 'tags1', 'value': ['AMBASSADOR', 'Ambassador', 'ambassador'] }
            ]
          });
        }

        console.log('[ASTR] filters 2', filters);
        console.log('[ASTR] products_per_page: ', window.theme.collections.products_per_page, ' page: ', window.theme.collections.page);

        const from_page = (Number(window.theme.collections.products_per_page) * (window.theme.collections.page - 1)) ?? 0;

        // https://search.nosto.com/v1/graphql?ref=InputSearchQuery
        // https://my.nosto.com/admin/shopify-24836632/search/settings/indexedFields
        const search_query = {
          categoryId: String(this.collection_id),
          fields: this.fields,
          size: Number(window.theme.collections.products_per_page),
          from: from_page,
          facets: ['*'],
          filter: filters,
          sort: this.getSortConfig(),
        };

        // This works but not perfect as the currencies in Nosto and Shopify might not be fully synced, so we just request the USD prices and then convert within the product tile template
        // if (!window.Shopify.shop.includes('staging')) {
        //   search_query.currency = window.theme.currency_cart;
        // }

        console.log('[ASTR] search_query', search_query);

        // variation_id must always reflect your catalog / primary currency (ASTR: USD) — not the visitor’s currency.
        // nosto_variation must reflect the active/presentment currency for the current visitor — this is how Nosto knows which currency to display.
        nostojs(api => {
          // api.defaultSession().setVariation(window.theme.currency_cart),
          // api.setTaggingProvider('variation', window.theme.currency_cart),
          api.search({
            products: search_query
          }, {
            track: 'category',
            redirect: true
          }).then(response => {
            console.log('[ASTR] Nosto collection response', response);

            // Handle redirects if not already on the destination page
            if (response.redirect && !window.location.href.includes(response.redirect)) {
              window.location.href = response.redirect;
              return resolve([]);
            }

            resolve(formatProductsData(response));
          });
        });
      } catch (error) {
        console.log('NostoCollections Error', error);
        resolve([]);
      }
    });
  };
}
